/**
 * SocialHub - Main social media page (like Instagram)
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores';
import { SocialFeed, CreateReport, ReporterProfile } from '../components';

type View = 'feed' | 'explore' | 'create' | 'profile' | 'user-profile';

interface SocialHubProps {
  onBack?: () => void;
}

export function SocialHub({ onBack }: SocialHubProps) {
  const { user, profile } = useAuthStore();
  const [currentView, setCurrentView] = useState<View>('feed');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showCreateReport, setShowCreateReport] = useState(false);

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setCurrentView('user-profile');
  };

  const handleBackFromProfile = () => {
    setSelectedUserId(null);
    setCurrentView('feed');
  };

  return (
    <div className="min-h-screen bg-app-bg pb-20">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-app-bg/80 backdrop-blur-xl border-b border-glass-border">
        <div className="flex items-center justify-between px-4 py-3">
          {onBack && (
            <button onClick={onBack} className="text-white hover:text-hud-primary">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Blurp.ie" className="h-8" />
            <span className="font-bold text-lg text-white">Blurp.ie</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative text-white hover:text-hud-primary">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center">
                3
              </span>
            </button>
            {/* Messages */}
            <button className="text-white hover:text-hud-primary">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <AnimatePresence mode="wait">
        {currentView === 'user-profile' && selectedUserId ? (
          <motion.div
            key="user-profile"
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
          >
            <ReporterProfile
              userId={selectedUserId}
              onBack={handleBackFromProfile}
              onReportClick={(id) => console.log('Report clicked:', id)}
            />
          </motion.div>
        ) : currentView === 'profile' ? (
          <motion.div
            key="my-profile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {user && (
              <ReporterProfile
                userId={user.id}
                onReportClick={(id) => console.log('Report clicked:', id)}
              />
            )}
          </motion.div>
        ) : currentView === 'explore' ? (
          <motion.div
            key="explore"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <SocialFeed
              type="explore"
              onUserClick={handleUserClick}
              onReportClick={(id) => console.log('Report clicked:', id)}
            />
          </motion.div>
        ) : (
          <motion.div
            key="feed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Welcome banner for new users */}
            <div className="mx-4 mt-4 p-4 bg-gradient-to-r from-hud-primary/20 to-teal-500/20 border border-hud-primary/30 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-hud-primary/20 flex items-center justify-center">
                  <span className="text-2xl">🎤</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Welcome, Reporter!</h3>
                  <p className="text-sm text-text-secondary">
                    Your ID: <span className="font-mono text-hud-primary">{profile?.reporter_id || 'SR-????'}</span>
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateReport(true)}
                  className="px-4 py-2 bg-hud-primary text-black rounded-xl font-semibold text-sm"
                >
                  File Report
                </button>
              </div>
            </div>

            <SocialFeed
              type="explore"
              onUserClick={handleUserClick}
              onReportClick={(id) => console.log('Report clicked:', id)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-app-bg/90 backdrop-blur-xl border-t border-glass-border z-30">
        <div className="flex items-center justify-around py-2">
          {[
            { key: 'feed', icon: '🏠', label: 'Home' },
            { key: 'explore', icon: '🔍', label: 'Explore' },
            { key: 'create', icon: '➕', label: 'Report', special: true },
            { key: 'leaderboard', icon: '🏆', label: 'Top' },
            { key: 'profile', icon: '👤', label: 'Profile' },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => {
                if (item.key === 'create') {
                  setShowCreateReport(true);
                } else {
                  setCurrentView(item.key as View);
                }
              }}
              className={cn(
                'flex flex-col items-center gap-1 p-2 rounded-xl transition-colors',
                item.special
                  ? 'bg-hud-primary text-black -mt-4 shadow-lg shadow-hud-primary/30'
                  : currentView === item.key
                  ? 'text-hud-primary'
                  : 'text-text-secondary hover:text-white'
              )}
            >
              <span className={cn('text-xl', item.special && 'text-2xl')}>{item.icon}</span>
              <span className="text-xs">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Create Report Modal */}
      <CreateReport
        isOpen={showCreateReport}
        onClose={() => setShowCreateReport(false)}
        onSuccess={() => {
          // Refresh feed
        }}
      />
    </div>
  );
}
